import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaDownload, FaWhatsapp, FaEnvelope, FaTrash, FaPhone, FaMapMarkerAlt, FaCalendar } from 'react-icons/fa';
import { AppContext } from '../../App';

const LeadsManager = () => {
  const { leads, setLeads } = useContext(AppContext);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLeads = leads.filter(lead => {
    if (filter !== 'all' && lead.calculatorType !== filter) return false;
    if (searchTerm) {
      return lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             lead.phone.includes(searchTerm) ||
             (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return true;
  });

  const handleDeleteLead = (index) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      const newLeads = leads.filter((_, i) => i !== index);
      setLeads(newLeads);
      toast.success('Lead deleted successfully!');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Phone', 'Email', 'Location', 'Calculator Type', 'Date'];
    const csvData = filteredLeads.map(lead => [
      lead.name,
      lead.phone,
      lead.email || '',
      lead.location || '',
      lead.calculatorType,
      new Date(lead.timestamp).toLocaleString()
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Leads exported successfully!');
  };

  const calculatorTypes = ['all', 'SIP', 'Lumpsum', 'SWP', 'Retirement', 'Child Education', 'Marriage', 'Home Loan', 'Step-Up SIP', 'Combo'];

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-primary">Lead Management</h2>
        <div className="flex gap-3">
          <button onClick={handleExportCSV} className="btn-secondary flex items-center gap-2">
            <FaDownload /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by name, phone or email..."
              className="input-field"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              className="input-field"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              {calculatorTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Calculators' : type}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-4">
          <p className="text-sm opacity-90">Total Leads</p>
          <p className="text-2xl font-bold">{leads.length}</p>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-4">
          <p className="text-sm opacity-90">This Month</p>
          <p className="text-2xl font-bold">
            {leads.filter(l => new Date(l.timestamp).getMonth() === new Date().getMonth()).length}
          </p>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-4">
          <p className="text-sm opacity-90">This Week</p>
          <p className="text-2xl font-bold">
            {leads.filter(l => {
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return new Date(l.timestamp) > weekAgo;
            }).length}
          </p>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-4">
          <p className="text-sm opacity-90">Today</p>
          <p className="text-2xl font-bold">
            {leads.filter(l => new Date(l.timestamp).toDateString() === new Date().toDateString()).length}
          </p>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calculator</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLeads.map((lead, idx) => (
                <motion.tr
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{lead.name}</div>
                    {lead.email && <div className="text-xs text-gray-500">{lead.email}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <a href={`tel:${lead.phone}`} className="text-primary hover:underline flex items-center gap-1">
                      <FaPhone size={12} /> {lead.phone}
                    </a>
                    <button
                      onClick={() => window.open(`https://wa.me/${lead.phone}?text=Hello ${lead.name}, thank you for your interest in Wisdom Consultancy. How can we help you?`, '_blank')}
                      className="text-green-600 hover:underline text-sm flex items-center gap-1 mt-1"
                    >
                      <FaWhatsapp size={12} /> WhatsApp
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    {lead.location && (
                      <div className="flex items-center gap-1 text-sm">
                        <FaMapMarkerAlt size={12} className="text-gray-400" />
                        {lead.location}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {lead.calculatorType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <FaCalendar size={12} />
                      {new Date(lead.timestamp).toLocaleDateString()}
                    </div>
                    <div className="text-xs">
                      {new Date(lead.timestamp).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => window.location.href = `mailto:${lead.email || 'Wisdom2secure@gmail.com'}?subject=Follow up for ${lead.calculatorType} Inquiry&body=Dear ${lead.name},%0D%0A%0D%0AThank you for your interest in Wisdom Consultancy.%0D%0A%0D%0AWe would like to discuss your ${lead.calculatorType} calculation further.%0D%0A%0D%0ABest regards,%0D%0AWisdom Consultancy Team`}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Send Email"
                      >
                        <FaEnvelope className="text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteLead(idx)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <FaTrash className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredLeads.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No leads found. Leads will appear here when users download calculator reports.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeadsManager;